import { Descriptions, PageHeader, Table, Typography, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import {
    chemblTargetExternalLink,
    chineseDrugLink,
    ingredientLink,
    keggTargetExternalLink,
    plantDetailLink,
    recipeLink,
    uniprotDetailLink,
    uniprotTargetExternalLink,
} from '../hooks/TableLink';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'lodash';
import useFetch from '../hooks/fetcher';

const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';

const { Title } = Typography;
const { Item } = Descriptions;

type DiseaseDetailType = {
    id: string;
    level1: string;
    level2: string;
    level3: string;
    level4: string;
    level1_description: string;
    level2_description: string;
    level3_description: string;
    level4_description: string;
};

type TargetDetailType = {
    protein: string;
    gene: string;
    chembl_id: string;
    uniprot_entry: string;
    kegg_id: string;
    pharmacological_action: string;
    druggable: string;
};

type IngredientsDetailType = {
    ingredients_id: string;
    compound_name: string;
    molecular_formula: string;
    mw: string;
    xlogp: string;
    hba: string;
    hbd: string;
    tpsa: string;
    rotatable_bond: string;
    lipinski_failure: string;
    atcs: string;
};

type HerbsDetailType = {
    botanical_name: string;
    chinese_pharmaceutical_name: string;
    pinyin: string;
    chinese_id: string;
    botanical_id: string;
};

type RecipesDetailType = {
    chinese_character: string;
    pinyin: string;
    english_name: string;
    recipes_id: string;
};

type DiseaseDetailResponseType = {
    detail: DiseaseDetailType[];
    targets: TargetDetailType[];
    ingredients: IngredientsDetailType[];
    herbs: HerbsDetailType[];
    recipes: RecipesDetailType[];
};

// const uniprotTags = (uniprotList: string) => {
//     return uniprotList.split(',').map((item: string, key: number) => {
//         return (
//             <Link to={`/target/${item}`} key={key}>
//                 <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
//                     {item}
//                 </Tag>
//             </Link>
//         );
//     });
// };
const atcTags = (atcList?: string) => {
    if (_.isNil(atcList)) {
        return 'NA';
    }
    return atcList.split(',').map((item: string, key: number) => {
        return (
            <a
                href={`https://www.whocc.no/atc_ddd_index/?code=+${item}+&showdescription=no`}
                target="_blank"
                rel="noopener noreferrer"
                key={key}
            >
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </a>
        );
    });
};
const targetsTableColumns = [
    { title: 'Targets', dataIndex: 'protein', key: 'protein', render: uniprotDetailLink },
    { title: 'Gene', dataIndex: 'gene', key: 'gene' },
    { title: 'ChEMBL ID', dataIndex: 'chembl_id', key: 'chembl_id', render: chemblTargetExternalLink },
    { title: 'UNIPROT ID', dataIndex: 'uniprot_entry', key: 'uniprot_entry', render: uniprotTargetExternalLink },
    { title: 'KEGG ID', dataIndex: 'kegg_id', key: 'kegg_id', render: keggTargetExternalLink },
    { title: 'Pharmacological action', dataIndex: 'pharmacological_action', key: 'pharmacological_action' },
    { title: 'Druggable', dataIndex: 'druggable', key: 'druggable' },
];

const ingredientsTableColumns = [
    {
        title: 'Ingredients Id',
        key: 'ingredients_id',
        dataIndex: 'ingredients_id',
        // eslint-disable-next-line react/display-name
        render: ingredientLink,
    },
    { title: 'Ingredients Name', key: 'compound_name', dataIndex: 'compound_name' },
    { title: 'Formula', key: 'molecular_formula', dataIndex: 'molecular_formula' },
    {
        title: 'MW',
        key: 'mw',
        dataIndex: 'mw',
    },
    {
        title: 'XLogP',
        key: 'xlogp',
        dataIndex: 'xlogp',
    },
    {
        title: 'HBA',
        key: 'hba',
        dataIndex: 'hba',
    },
    {
        title: 'HBD',
        key: 'hbd',
        dataIndex: 'hbd',
    },
    {
        title: 'TPSA',
        key: 'tpsa',
        dataIndex: 'tpsa',
    },
    {
        title: 'Rotatable Bond',
        key: 'rotatable_bond',
        dataIndex: 'rotatable_bond',
    },
    {
        title: 'Lipinski Failure',
        key: 'lipinski_failure',
        dataIndex: 'lipinski_failure',
    },
    { title: 'ATC Codes', key: 'atcs', dataIndex: 'atcs', render: atcTags },
];

const herbsTableColumns = [
    { title: 'Accepted botanical name', dataIndex: 'botanical_name', key: 'botanical_name', render: plantDetailLink },
    {
        title: 'Chinese pharmaceutical name',
        dataIndex: 'chinese_pharmaceutical_name',
        key: 'chinese_pharmaceutical_name',
        render: chineseDrugLink,
    },
    { title: 'Transliteration', dataIndex: 'pinyin', key: 'pinyin' },
];

const recipesTableColumns = [
    { title: 'Chinese character', dataIndex: 'chinese_character', key: 'chinese_character', render: recipeLink },
    { title: 'Transliteration', key: 'pinyin', dataIndex: 'pinyin' },
    { title: 'English name', dataIndex: 'english_name', key: 'english_name' },
];

const PageDiseaseDetail: React.FC = () => {
    const { id: diseaseId } = useParams<any>();
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const [result, setResult] = useState<DiseaseDetailResponseType>();
    const history = useHistory();

    const goToRecipeDetail = (keyword: string, placeholder: string): void => {
        history.push(`/recipe/detail/${placeholder}/${keyword}`);
    };

    useEffect(() => {
        get('/api/disease_detail.php', { id: diseaseId }).then((r) => setResult(r));
    }, []);
    const d = result?.detail[0];
    const targets = result?.targets;
    const ingredients = result?.ingredients;
    const icd10 = result && `Chapter: ${d?.level1} / Section: ${d?.level2} / Block: ${d?.level3} / ${d?.level4}`;
    return (
        <div>
            <PageHeader title="Disease" subTitle={icd10} style={{ marginBottom: '3vh' }} />
            <Descriptions
                title={<Title level={4}>ICD-10-CM Details</Title>}
                bordered
                colon
                column={1}
                size="small"
                style={{ marginBottom: '3vh' }}
            >
                <Item label={d?.level1}>{d?.level1_description}</Item>
                <Item label={d?.level2}>{d?.level2_description}</Item>
                <Item label={d?.level3}>{d?.level3_description}</Item>
                <Item label={d?.level4}>{d?.level4_description}</Item>
            </Descriptions>

            <Title level={4}>Related ingredients</Title>
            <Table
                dataSource={ingredients}
                columns={ingredientsTableColumns}
                // rowKey={(record) => record.uniprot_entries}
                style={{ marginBottom: '3vh' }}
            />

            <Title level={4}>Related Targets</Title>
            <Table
                dataSource={targets}
                columns={targetsTableColumns}
                rowKey="uniprot_entry"
                style={{ marginBottom: '3vh' }}
            />

            <Title level={4}>Related herbs</Title>
            <Table
                dataSource={result?.herbs}
                columns={herbsTableColumns}
                rowKey="chinese_pharmaceutical_name"
                style={{ marginBottom: '3vh' }}
            />

            <Title level={4}>Related recipes</Title>
            <Table
                dataSource={result?.recipes}
                columns={recipesTableColumns}
                rowKey="english_name"
                onRow={(record) => ({
                    onClick: () => {
                        goToRecipeDetail(record.recipes_id, record.chinese_character);
                    },
                    style: { cursor: 'pointer' },
                })}
                style={{ marginBottom: '3vh' }}
            />
        </div>
    );
};

export default PageDiseaseDetail;
